/**
 * MobileJoinQrDialog — shows the mobile join QR for a pending invitation
 * (#1244 slice E4, design §5.4).
 *
 * Only reachable through resend, because the invitation token is hashed at rest
 * and can therefore only be rendered at mint time. The dialog says so: whoever
 * already holds the previous link needs the new code.
 *
 * It carries the web invitation link as well as the QR, and that is not
 * decoration. Resend has already rotated the token by the time this opens, so
 * this dialog holds the only copy of it. If email delivery failed, or the
 * payload is too large to encode as a QR at all, the link is the sole remaining
 * way for the invitee to get in — a dialog that showed neither would silently
 * strand the account.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useNotify } from '@/stores/notificationStore';
import { MobileJoinQr } from './MobileJoinQr';

export interface MobileJoinQrTarget {
  email: string;
  /**
   * Absent when this deployment's staff-api predates `mobileJoinUrl` (#1244 E1).
   * The dialog still opens — the token has already rotated, so discarding the
   * response would strand the invitation with no code and no link.
   */
  mobileJoinUrl?: string;
  /** The rotated web invitation link, from the same resend response. */
  invitationUrl: string;
  expiresAt: string;
  /**
   * Whether the rotated invitation actually reached the invitee by email.
   * Delivery is best-effort server-side and the token has already rotated by
   * the time it fails, so a false here means the QR and the link below are the
   * only ways in.
   */
  emailSent: boolean;
}

interface MobileJoinQrDialogProps {
  target: MobileJoinQrTarget | null;
  onClose: () => void;
}

export function MobileJoinQrDialog({ target, onClose }: MobileJoinQrDialogProps) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const [linkCopied, setLinkCopied] = useState(false);

  const handleCopyLink = async () => {
    if (!target) return;
    try {
      await navigator.clipboard.writeText(target.invitationUrl);
      setLinkCopied(true);
      notify.success(t('linkCopied'));
      setTimeout(() => { setLinkCopied(false); }, 2000);
    } catch {
      // Fallback: the field is selectable.
    }
  };

  if (!target) return null;

  return (
    <ResponsiveDialog
      open
      onClose={onClose}
      mobileTitle={t('mobileJoinQr.dialogTitle')}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ display: { xs: 'none', sm: 'block' } }}>
        {t('mobileJoinQr.dialogTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info">{t('mobileJoinQr.rotatedNote')}</Alert>
          {!target.emailSent && (
            <Alert severity="warning">{t('mobileJoinQr.emailFailed')}</Alert>
          )}
          {target.mobileJoinUrl ? (
            <MobileJoinQr
              mobileJoinUrl={target.mobileJoinUrl}
              email={target.email}
              expiresAt={target.expiresAt}
              // Same reasoning as the invite dialog: this is a multi-day invitation
              // token, and copy-and-paste is the only route into the app that
              // survives a mail client rewriting the link.
              enableCopy
            />
          ) : (
            <Alert severity="warning">{t('mobileJoinQr.unavailable')}</Alert>
          )}

          {/*
            The rotated web link. Always present, because this dialog holds the
            only copy of the new token and the QR is not always available.
          */}
          <Box>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              {t('invitationLink')}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                value={target.invitationUrl}
                size="small"
                fullWidth
                slotProps={{ input: { readOnly: true } }}
                sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
              />
              <Tooltip title={linkCopied ? t('linkCopied') : t('copyLink')}>
                <IconButton
                  onClick={() => void handleCopyLink()}
                  size="small"
                  aria-label={t('copyLink')}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={onClose}>
          {tc('close')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
